/**
 * What lies on the maze's tables (design D2).
 *
 * A profile names the kinds it wants left about — `["screwdriver"]` — and this decides
 * which table each one sits on, drawn from the level's own seed like everything else in a
 * maze. Hand-authoring a position would not survive a seed change: the dead end the table
 * stood in would have moved.
 *
 * The result is `PlacedObject`s, the same shape `buildGeometry` makes from a profile's
 * authored objects. That is the whole point of building them here rather than hanging them
 * off `grounds`: every existing path — mounting, the interaction ray, the carry controller,
 * the pocket — picks them up with no change at all.
 *
 * Pure and headless, like the rest of `src/game/`.
 */

import { mulberry32, seedFromString } from '../../utils/seededRandom';
import { OBJECT_KINDS, PLACEMENT_CLEARANCE } from '../objectKinds';
import type { PlacedObject } from '../profile/buildGeometry';
import type { ObjectKind } from '../profile/types';
import type { Table } from './placeTables';

/**
 * One item per kind, each on a table of its own, in the order the kinds were declared.
 *
 * Ids are `${kind}-${index}` — derived, and stable across launches, because the pocket
 * keys its slots by id and an id that drifted between runs would show up only as a lost
 * item. More kinds than tables places what fits; validation rejects that profile before
 * it ever gets here.
 */
export function placeTableItems(
  tables: readonly Table[],
  seed: string,
  kinds: readonly ObjectKind[],
): PlacedObject[] {
  if (kinds.length === 0 || tables.length === 0) return [];

  // Namespaced like `generateMaze`'s 'maze:' draw, `placeLadders`' and `placeTables`', so
  // adding items to a level cannot shift where its hedges or its tables ended up.
  const random = mulberry32(seedFromString(`table-items:${seed}`));
  const shuffled = [...tables];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }

  return kinds.slice(0, shuffled.length).map((kind, index) => {
    const table = shuffled[index]!;
    const size = OBJECT_KINDS[kind].size;
    return {
      id: `${kind}-${index}`,
      kind,
      box: {
        center: {
          x: table.center.x,
          y: table.topHeight + size.y / 2 + PLACEMENT_CLEARANCE,
          z: table.center.z,
        },
        size,
      },
    };
  });
}
